import { PRIVATE_TODO_TITLE_KEY } from '@/common/constants/constants';
import {
    ActiveStatusCodes,
    CompleteStatusCodes,
    StatusCode,
} from '@/common/constants/status';
import {
    Todo,
    TodoWithDetails,
    TodoWithGoal,
    TodoWithUser,
} from '@/common/types/databaseTypes';
import {
    getEndOfDay,
    getStartOfDay,
} from '@tickitacka/shared';
import { authService } from '@/features/auth/services/authService';
import { goalService } from '@/features/goal/services/goalService';
import { supabase } from '@tickitacka/shared';
import { useAuthStore } from '@/stores/authStore';
import { useCategoryStore } from '@/stores/categoryStore';
import { v4 as uuidv4 } from 'uuid';
import { workspaceService } from '@/features/workspace/services/workspaceService';

// export type CompleteDateFiler =
//     | 'none' // 모두 가리기
//     | 'today'
//     | 'yesterday'
//     | 'week'
//     | 'month';

interface GetTodosOptions {
    userId: string | undefined;
    // completeDateFilter?: CompleteDateFiler;
}

export interface CreateTodoInput {
    goal_id: string | null;
    title: string;
    register_id: string;
    user_id: string;
    category_id?: string;
    is_public?: boolean;
    content?: string;
    enable?: boolean;
}

/**
 * Todo 조회에 사용되는 공통 select 쿼리 문자열을 반환합니다.
 */
const getTodoSelectQuery = (): string => `
    *,
    goal:goal_id(
        id,
        title,
        status_code,
        priority_code,
        indicator_type,
        current_value,
        target_value,
        register_date,
        start_date,
        end_date,
        team_id
    ),
    project:goal_id(
        project:project_id(
            id,
            name
        )
    ),
    leader:goal_id(
        leader:leader_id(
            id,
            name,
            profile_image_url
        )
    ),
    planners:planner(
        id,
        planner_date,
        blocks
    ),
    category:category_id(
        id,
        name,
        color,
        color_name
    ),
    comment_count:comment(id,enable),
    note_count:note(id,enable)
`;

/**
 * Supabase에서 반환된 원시 Todo 데이터 타입
 */
interface RawTodoData {
    [key: string]: unknown;
    id: string;
    goal_id?: string;
    title?: string;
    project?: {
        project?: {
            id: string;
            name: string;
        };
    };
    leader?: {
        leader?: {
            id: string;
            name: string;
            profile_image_url: string;
        };
    };
    goal?: {
        id: string;
        title: string;
        status_code: string;
        priority_code: string;
        indicator_type: string | null;
        current_value: number | null;
        target_value: number | null;
        register_date: string;
        start_date: string | null;
        end_date: string | null;
        team_id: string;
    };
    category?: {
        id: string;
        name: string;
        color: string;
        color_name: string;
    };
    planners?: {
        id: string;
        planner_date: string;
        blocks: unknown;
    }[];
}

/**
 * 데이터가 RawTodoData 타입인지 확인하는 타입 가드 함수
 */
function isRawTodoData(data: unknown): data is RawTodoData {
    return (
        data !== null &&
        typeof data === 'object' &&
        'id' in data &&
        typeof (data as Record<string, unknown>).id === 'string'
    );
}

/**
 * 데이터 배열에서 유효한 RawTodoData 항목만 필터링
 */
function filterValidTodoData(data: unknown[]): RawTodoData[] {
    return (data || []).filter(isRawTodoData);
}

/**
 * 단일 Todo 데이터 변환
 */
function transformSingleTodo(data: RawTodoData): TodoWithDetails {
    // 카테고리 값이 없으면 기본 카테고리 지정
    let category = data.category;
    if (!category) {
        const categories = useCategoryStore.getState().categories;
        if (categories && categories.length > 0) {
            category = categories[0];
        }
    }

    const user = useAuthStore.getState().user;
    if (!data.is_public && user?.id !== data.user_id) {
        data.title = PRIVATE_TODO_TITLE_KEY;
    }

    const enabledCommentsCount = data.comment_count
        ? (data.comment_count as { id: string; enable: boolean }[]).filter(
              comment => comment?.enable === true
          ).length
        : 0;
    const enabledNotescount = data.note_count
        ? (data.note_count as { id: string; enable: boolean }[]).filter(
              note => note?.enable === true
          ).length
        : 0;

    return {
        ...data,
        category,
        project: data.project?.project,
        leader: data.leader?.leader,
        comments_count: enabledCommentsCount,
        notes_count: enabledNotescount,
    } as TodoWithDetails;
}

/**
 * Todo 배열 데이터 변환
 */
function transformTodoArray(data: RawTodoData[]): TodoWithDetails[] {
    return data.map(transformSingleTodo);
}

/**
 * 중첩된 Todo 데이터 구조를 변환합니다.
 * @param data - Supabase에서 반환된 원본 데이터
 * @returns 변환된 TodoWithDetails 데이터
 */
function transformTodoData(data: RawTodoData[]): TodoWithDetails[];
function transformTodoData(data: RawTodoData): TodoWithDetails;
function transformTodoData(
    data: RawTodoData | RawTodoData[]
): TodoWithDetails | TodoWithDetails[] {
    if (Array.isArray(data)) {
        return transformTodoArray(data);
    } else {
        return transformSingleTodo(data);
    }
}

export const todoService = {
    async getInboxTodos({
        userId,
    }: GetTodosOptions): Promise<TodoWithDetails[]> {
        if (!userId) return [];

        try {
            // 숨겨진 목표 ID 가져오기
            const hiddenGoalIds =
                await goalService.getHiddenGoalsByUserId(userId);

            const userTodosQuery = supabase
                .from('todo')
                .select(getTodoSelectQuery())
                .eq('user_id', userId)
                .eq('enable', true)
                .is('planners', null)
                .is('complete_date', null)
                .is('cancelled_date', null)
                .order('register_date', { ascending: false });

            // 숨겨진 목표에 연결된 Todo를 제외
            if (hiddenGoalIds.length > 0) {
                // .or 메소드를 사용하여 "NOT IN 조건" 또는 "IS NULL 조건" 조합
                userTodosQuery.or(
                    `goal_id.not.in.(${hiddenGoalIds.join(',')}),goal_id.is.null`
                );
            }

            const { data: userTodos, error } = await userTodosQuery;

            if (error) throw error;

            const validData = filterValidTodoData(
                Array.isArray(userTodos) ? userTodos : []
            );

            // 비동기 작업을 위해 Promise.all 사용
            const accessCheckResults = await Promise.all(
                validData.map(async todo => {
                    if (!todo.goal?.team_id) {
                        return { todo, isAllowed: true }; // 목표가 없는 경우는 항상 허용
                    }

                    const { isAllowed } =
                        await workspaceService.checkUserWorkspaceMembership(
                            todo.goal.team_id,
                            userId
                        );

                    return { todo, isAllowed };
                })
            );

            // 접근 권한이 있는 Todo만 필터링
            const accessibleData = accessCheckResults
                .filter(result => result.isAllowed)
                .map(result => result.todo);

            // goal이 null이거나 active 상태인 goal의 todo만 필터링
            const filteredData = accessibleData.filter(
                todo =>
                    !todo.goal ||
                    ActiveStatusCodes.includes(
                        todo.goal.status_code as StatusCode
                    )
            );

            return transformTodoData(filteredData);
        } catch (e) {
            console.error('Failed to fetch todos', e);
            throw e;
        }
    },

    async getNotDoneTodos(
        userId: string,
        goalIds: string[]
    ): Promise<TodoWithGoal[]> {
        if (!userId) return [];

        try {
            const { data, error } = await supabase
                .from('todo')
                .select(
                    `
                        *,
                        goal:goal_id(
                            id,
                            title,
                            status_code
                        )
                    `
                )
                .eq('user_id', userId)
                .is('complete_date', null)
                .is('cancelled_date', null)
                .eq('enable', true)
                .in('goal_id', goalIds)
                .order('register_date', { ascending: false });

            if (error) throw error;

            // goal이 null이거나 active 상태인 goal의 todo만 필터링
            const filteredData = ((data || []) as TodoWithGoal[]).filter(
                todo =>
                    !todo.goal ||
                    ActiveStatusCodes.includes(
                        todo.goal.status_code as StatusCode
                    )
            );

            return filteredData;
        } catch (e) {
            console.error('Failed to fetch todos', e);
            throw e;
        }
    },

    async getDoneTodos(
        userId: string,
        goalIds: string[]
    ): Promise<TodoWithGoal[]> {
        if (!userId) return [];
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);

        try {
            const { data, error } = await supabase
                .from('todo')
                .select(
                    `
                        *,
                        goal:goal_id(
                            id,
                            title,
                            status_code
                        )
                    `
                )
                .eq('user_id', userId)
                .or(
                    `cancelled_date.gte.${monthAgo.toISOString()},complete_date.gte.${monthAgo.toISOString()}`
                )
                .is('enable', true)
                .in('goal_id', goalIds)
                .order('register_date', { ascending: false });

            if (error) throw error;

            // goal이 null이거나 active, done 상태인 goal의 todo만 필터링
            const filteredData = ((data || []) as TodoWithGoal[]).filter(
                todo =>
                    !todo.goal ||
                    [...ActiveStatusCodes, ...CompleteStatusCodes].includes(
                        todo.goal.status_code as StatusCode
                    )
            );

            return filteredData;
        } catch (e) {
            console.error('Failed to fetch todos', e);
            throw e;
        }
    },

    async getPlannerTodos(userId: string): Promise<TodoWithDetails[]> {
        if (!userId) return [];

        const todayStart = getStartOfDay(new Date());
        const todayEnd = getEndOfDay(new Date());

        try {
            const userTodosQuery = supabase
                .from('todo')
                .select(getTodoSelectQuery())
                .eq('user_id', userId)
                .is('planners', null)
                .is('complete_date', null)
                .is('cancelled_date', null)
                .eq('enable', true)
                .gte('register_date', todayStart)
                .lte('register_date', todayEnd)
                .order('register_date', { ascending: false });

            const { data: userTodos, error } = await userTodosQuery;

            if (error) throw error;

            const validData = filterValidTodoData(
                Array.isArray(userTodos) ? userTodos : []
            );

            return transformTodoData(validData);
        } catch (e) {
            console.error('Failed to fetch todos', e);
            throw e;
        }
    },

    async getTodoWithDetailsById(todoId: string): Promise<TodoWithDetails> {
        try {
            const { data, error } = await supabase
                .from('todo')
                .select(getTodoSelectQuery())
                .eq('id', todoId)
                .eq('enable', true)
                .maybeSingle();

            if (error) throw error;

            // 데이터 유효성 검사
            if (!isRawTodoData(data)) {
                return null;
                throw new Error(`Invalid todo data for id: ${todoId}`);
            }

            return transformTodoData(data);
        } catch (error) {
            console.error(`Failed to fetch todo ${todoId}`, error);
            throw error;
        }
    },

    /**
     * Todo 데이터만 불러옴, enable false여도 불러옴
     * enable false 상태로 새로 추가한 투두를 불러올 때 등에 사용
     * @param todoId
     * @returns Todo
     */
    async getTodoById(todoId: string): Promise<Todo> {
        try {
            const { data, error } = await supabase
                .from('todo')
                .select('*')
                .eq('id', todoId)
                .maybeSingle();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error(`Failed to fetch added todo ${todoId}`, error);
            throw error;
        }
    },

    async createTodo(input: CreateTodoInput): Promise<TodoWithUser> {
        const categories = useCategoryStore.getState().categories;

        try {
            const { data, error } = await supabase
                .from('todo')
                .insert({
                    ...input,
                    id: uuidv4(),
                    register_date: new Date().toISOString(),
                    update_date: new Date().toISOString(),
                    update_id: input.register_id,
                    category_id: input.category_id ?? categories?.[0]?.id, // 기본 카테고리 지정
                    // enable: true,
                })
                .select(
                    `
                    *,
                    user:user_id(
                        id,
                        name,
                        profile_image_url
                    )
                `
                )
                .single();

            if (error) throw error;

            // 트리거로 처리
            // Todo가 특정 목표(Goal)에 연결되었을 경우, goal_participant 테이블에 참여자 추가
            // if (input.goal_id) {
            //     // upsert를 사용하여 이미 있는 경우는 업데이트하고, 없는 경우는 삽입
            //     const { error: participantError } = await supabase
            //         .from('goal_participant')
            //         .upsert(
            //             {
            //                 goal_id: input.goal_id,
            //                 user_id: input.user_id,
            //                 register_id: input.register_id,
            //                 register_date: new Date().toISOString(),
            //                 update_id: input.register_id,
            //                 update_date: new Date().toISOString(),
            //             },
            //             { onConflict: 'goal_id,user_id' } // 이미 동일한 goal_id와 user_id 조합이 있는 경우 충돌 처리
            //         );

            //     if (participantError) {
            //         console.error(
            //             '목표 참여자 추가 중 오류 발생:',
            //             participantError
            //         );
            //     }
            // }
            return data;
        } catch (error) {
            console.error('Failed to create todo', error);
            throw error;
        }
    },

    async updateTodo(
        id: string,
        fields: Partial<Todo>
    ): Promise<TodoWithDetails> {
        const { user } = useAuthStore.getState();

        try {
            // const { goal, planner, ...todoFields } = updatedTodo;

            const { data, error } = await supabase
                .from('todo')
                .update({
                    ...fields,
                    update_id: user?.id ?? '',
                    update_date: new Date().toISOString(),
                })
                .eq('id', id)
                .select(getTodoSelectQuery())
                .single();

            if (error) throw error;

            if (!isRawTodoData(data)) {
                throw new Error(`Invalid todo data for id: ${id}`);
            }

            return transformTodoData(data);
        } catch (error) {
            console.error('Failed to update todo', error);
            throw error;
        }
    },

    async updateGoalParticipant(
        userId: string,
        registerId: string,
        goalId: string
    ): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('todo')
                .update({
                    goal_id: null,
                    update_date: new Date().toISOString(),
                    update_id: registerId,
                })
                .match({
                    user_id: userId,
                    goal_id: goalId,
                    enable: true,
                });
            if (error) throw error;
            return true;
        } catch (error) {
            console.error('Failed to update goal participant', error);
            return false;
        }
    },

    async deleteTodo(todoId: string): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            const userId = user?.id;

            const { error } = await supabase
                .from('todo')
                // .delete()
                .update({
                    enable: false,
                    update_date: new Date().toISOString(),
                    update_id: userId,
                })
                .eq('id', todoId);

            if (error) throw error;
            return;
        } catch (error) {
            console.error(`Failed to delete todo ${todoId}`, error);
            throw error;
        }
    },

    async findByGoalId(goalId: string): Promise<TodoWithDetails[]> {
        try {
            const { data, error } = await supabase
                .from('todo')
                .select(getTodoSelectQuery())
                .eq('goal_id', goalId)
                .eq('enable', true);

            if (error) throw error;

            const validData = filterValidTodoData(
                Array.isArray(data) ? data : []
            );
            return transformTodoData(validData);
        } catch (error) {
            console.error(`Failed to fetch todos for goal ${goalId}`, error);
            throw error;
        }
    },

    // 완료되지 않은 모든 Todo와 특정 날짜에 완료된 Todo 함께 조회
    async getAllTodosWithPlanners(
        userId: string,
        selectedDate?: Date
    ): Promise<TodoWithDetails[]> {
        try {
            let query = supabase
                .from('todo')
                .select(getTodoSelectQuery())
                .eq('user_id', userId)
                .eq('enable', true)
                .is('cancelled_date', null);

            // 완료되지 않은 할 일 또는 선택된 날짜에 완료된 할 일 필터링
            if (selectedDate) {
                const startOfDay = getStartOfDay(selectedDate);
                const endOfDay = getEndOfDay(selectedDate);

                // complete_date가 null이거나 선택된 날짜인 경우
                query = query.or(
                    `complete_date.is.null,and(complete_date.gte.${startOfDay},complete_date.lte.${endOfDay})`
                );
            } else {
                // 날짜 미선택 시 미완료 항목만
                query = query.is('complete_date', null);
            }

            const { data, error } = await query.order('register_date', {
                ascending: false,
            });

            if (error) throw error;

            const validData = filterValidTodoData(
                Array.isArray(data) ? data : []
            );
            return transformTodoData(validData);
        } catch (error) {
            console.error('Failed to fetch todos with planners', error);
            throw error;
        }
    },
};
